import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../api/ApiProvider.js';
import { TenantNotApprovedError } from '../../api/client.js';
import type { ClassroomDto, ClassroomStatus } from '../../api/types.js';
import { ClassroomCard } from '../Classrooms/ClassroomCard.js';
import { CreateClassroomForm } from '../Classrooms/CreateClassroomForm.js';
import { Modal } from '../../components/Modal/Modal.js';
import styles from './ClassroomsTab.module.css';

export interface ClassroomsTabProps {
  /** Selecting a classroom card hands its id back to the hub for the
   *  detail/configuration screen. */
  onSelectClassroom: (classroomId: string) => void;
}

export function ClassroomsTab({ onSelectClassroom }: ClassroomsTabProps) {
  const api = useApi();
  const [statusFilter, setStatusFilter] = useState<ClassroomStatus>('active');
  const [createOpen, setCreateOpen] = useState(false);

  const query = useQuery({
    queryKey: ['classrooms', statusFilter],
    queryFn: () => api.classrooms.list(statusFilter),
    retry: (failureCount, error) => !(error instanceof TenantNotApprovedError) && failureCount < 2,
  });

  if (query.error instanceof TenantNotApprovedError) {
    return (
      <div className={`glass glass--solid ${styles.empty}`}>
        <h2>Pending approval</h2>
        <p>
          Your school hasn&rsquo;t been approved yet. Once an administrator approves your tenant,
          you&rsquo;ll be able to create classrooms.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.tab}>
      <div className={styles.toolbar}>
        <div className={styles.filters} role="tablist" aria-label="Classroom status">
          <button
            type="button"
            role="tab"
            aria-selected={statusFilter === 'active'}
            onClick={() => setStatusFilter('active')}
            className={statusFilter === 'active' ? styles.filterActive : styles.filter}
            data-testid="filter-active"
          >
            Active
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={statusFilter === 'archived'}
            onClick={() => setStatusFilter('archived')}
            className={statusFilter === 'archived' ? styles.filterActive : styles.filter}
            data-testid="filter-archived"
          >
            Archived
          </button>
        </div>
        <button
          type="button"
          className={`glass-pill glass-pill--accent ${styles.addButton}`}
          onClick={() => setCreateOpen(true)}
          data-testid="classroom-add-button"
        >
          <span aria-hidden="true">+</span> Add classroom
        </button>
      </div>

      {query.isLoading ? (
        <div className={`glass glass--solid ${styles.empty}`}>
          <p>Loading classrooms…</p>
        </div>
      ) : null}

      {query.error && !(query.error instanceof TenantNotApprovedError) ? (
        <div className={`glass glass--solid ${styles.empty}`} role="alert">
          <p>{query.error instanceof Error ? query.error.message : 'Failed to load classrooms.'}</p>
        </div>
      ) : null}

      {query.data && query.data.length === 0 ? (
        <div className={`glass glass--solid ${styles.empty}`} data-testid="classrooms-empty">
          <p>
            {statusFilter === 'active'
              ? 'No active classrooms yet — create one below to get started.'
              : 'No archived classrooms.'}
          </p>
        </div>
      ) : null}

      {query.data && query.data.length > 0 ? (
        <ul className={styles.grid} aria-label="Classrooms">
          {query.data.map((c: ClassroomDto) => (
            <li key={c.id}>
              <ClassroomCard classroom={c} onSelect={onSelectClassroom} />
            </li>
          ))}
        </ul>
      ) : null}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New classroom"
        description="Give it a name and a school year. You can add pages and pick a theme after."
        testId="classroom-create-modal"
      >
        <CreateClassroomForm
          onCreated={() => setCreateOpen(false)}
          onCancel={() => setCreateOpen(false)}
        />
      </Modal>
    </div>
  );
}
